import { getRectPort, getId } from './util'

class Group {
  constructor (graph) {
    this.ctrlPressed = false
    this.embedPadding = 20
    this.graph = graph
    this.init()
  }

  // 查找节点的子节点列表
  findChildNodes (node, nodeList) {
    return nodeList.filter((item) => {
      const itemId = item?.data?.id || item?.id
      return itemId?.includes(node.id) && itemId !== node.id
    })
  }

  // 更新节点及其子节点的原始位置
  updateOriginPositions (node, childList) {
    const currentPos = node.getPosition()
    const previousPos = node.prop('originPosition')
    
    if (!previousPos) {
      node.prop('originPosition', currentPos)
      childList.forEach(childNode => {
        if (!childNode.prop('originPosition')) {
          childNode.prop('originPosition', childNode.getPosition())
        }
      })
      return false
    }
    
    const deltaX = currentPos.x - previousPos.x
    const deltaY = currentPos.y - previousPos.y
    
    if (Math.abs(deltaX) > 0.1 || Math.abs(deltaY) > 0.1) {
      childList.forEach(childNode => {
        childNode.prop('originPosition', childNode.getPosition())
      })
      node.prop('originPosition', currentPos)
      return true
    }
    return false
  }

  // 同步移动子节点
  syncMoveChildren (node, childList, deltaX, deltaY) {
    childList.forEach(childNode => {
      const childPos = childNode.getPosition()
      childNode.setPosition({
        x: childPos.x + deltaX,
        y: childPos.y + deltaY
      }, { skipParentHandler: true })
    })
  }

  init () {
    this.graph.on('node:embedding', ({ e }) => {
      this.ctrlPressed = e.metaKey || e.ctrlKey
    })

    this.graph.on('node:embedded', () => {
      this.ctrlPressed = false
    })

    this.graph.on('node:change:size', ({ node, options }) => {
      if (options.skipParentHandler) return
      const children = node.getChildren()
      if (children?.length) {
        node.prop('originSize', node.getSize())
      }
    })

    this.graph.on('node:change:position', ({ node, options }) => {
      if (options.skipParentHandler || this.ctrlPressed) return

      const currentPos = node.getPosition()
      let previousPos = node.prop('originPosition')
      
      // 处理组合节点移动
      if (node.getData()?.isGroup && node.getData()?.groupedNodes) {
        if (!previousPos) {
          node.prop('originPosition', currentPos)
          return
        }
        
        const deltaX = currentPos.x - previousPos.x
        const deltaY = currentPos.y - previousPos.y
        
        if (Math.abs(deltaX) > 0.1 || Math.abs(deltaY) > 0.1) {
          node.getData().groupedNodes.forEach(nodeId => {
            const childNode = this.graph.getCellById(nodeId)
            if (childNode) {
              const childPos = childNode.getPosition()
              childNode.setPosition({
                x: childPos.x + deltaX,
                y: childPos.y + deltaY
              }, { skipParentHandler: true })
            }
          })
          node.prop('originPosition', currentPos)
        }
        return
      }
      
      // 自动调整连接点
      this.autoAdjustConnections(node)
      
      // 处理父组件移动时同步移动隐藏的子组件
      const nodeList = this.graph.getNodes()
      const childList = this.findChildNodes(node, nodeList)
      
      if (childList.length > 0) {
        const draftComponent = window.draftComponent
        
        // 检查是否是框选拖拽
        if (draftComponent?.selection) {
          const selectedCells = draftComponent.selection.getSelectedCells()
          const hasChildSelected = childList.some(childNode => 
            selectedCells.some(cell => cell.id === childNode.id)
          )
          
          // 框选拖拽时，只更新原始位置，不移动子节点
          if (hasChildSelected && selectedCells.length > 1) {
            this.updateOriginPositions(node, childList)
            return
          }
        }
        
        // 单独拖拽父节点时，同步移动子节点
        if (!previousPos) {
          node.prop('originPosition', currentPos)
          return
        }
        
        const deltaX = currentPos.x - previousPos.x
        const deltaY = currentPos.y - previousPos.y
        
        if (Math.abs(deltaX) > 0.1 || Math.abs(deltaY) > 0.1) {
          this.syncMoveChildren(node, childList, deltaX, deltaY)
          node.prop('originPosition', currentPos)
        }
        return
      }
      
      // 处理分类分析中分类大框的移动
      if (node.getData()?.isCategoryContainer && node.getData()?.categoryId) {
        if (!previousPos) {
          node.prop('originPosition', currentPos)
          return
        }
        
        const deltaX = currentPos.x - previousPos.x
        const deltaY = currentPos.y - previousPos.y
        
        if (Math.abs(deltaX) > 0.1 || Math.abs(deltaY) > 0.1) {
          const allNodes = this.graph.getNodes()
          const categoryElements = allNodes.filter(n => 
            n.getData()?.parentGroup === `group_${node.getData().categoryId}` && 
            n.getData()?.isElement
          )
          
          categoryElements.forEach(elementNode => {
            const elementPos = elementNode.getPosition()
            elementNode.setPosition({
              x: elementPos.x + deltaX,
              y: elementPos.y + deltaY
            }, { skipParentHandler: true })
          })
          
          const groupNode = this.graph.getCellById(`group_${node.getData().categoryId}`)
          if (groupNode) {
            const groupPos = groupNode.getPosition()
            groupNode.setPosition({
              x: groupPos.x + deltaX,
              y: groupPos.y + deltaY
            }, { skipParentHandler: true })
          }
          
          node.prop('originPosition', currentPos)
        }
        return
      }

      // 处理 X6 原生父子关系
      const children = node.getChildren()
      if (children?.length) {
        node.prop('originPosition', node.getPosition())
      }

      const parent = node.getParent()
      if (parent?.isNode()) {
        let originSize = parent.prop('originSize')
        if (originSize == null) {
          originSize = parent.getSize()
          parent.prop('originSize', originSize)
        }

        let originPosition = parent.prop('originPosition')
        if (originPosition == null) {
          originPosition = parent.getPosition()
          parent.prop('originPosition', originPosition)
        }

        let x = originPosition.x
        let y = originPosition.y
        let cornerX = originPosition.x + originSize.width
        let cornerY = originPosition.y + originSize.height
        let hasChange = false

        const parentChildren = parent.getChildren()
        if (parentChildren) {
          parentChildren.forEach((child) => {
            const bbox = child.getBBox().inflate(this.embedPadding)
            const corner = bbox.getCorner()

            if (bbox.x < x) {
              x = bbox.x
              hasChange = true
            }
            if (bbox.y < y) {
              y = bbox.y
              hasChange = true
            }
            if (corner.x > cornerX) {
              cornerX = corner.x
              hasChange = true
            }
            if (corner.y > cornerY) {
              cornerY = corner.y
              hasChange = true
            }
          })
        }

        if (hasChange) {
          parent.prop({
            position: { x, y },
            size: { width: cornerX - x, height: cornerY - y }
          }, { skipParentHandler: true })
        }
      }
    })
  }

  addGroup () {
    const id = getId()
    this.graph.addNode({
      id,
      x: 40,
      y: 40,
      width: 360,
      height: 160,
      zIndex: 0,
      label: '元素组',
      attrs: {
        body: {
          fill: '#fffbe6',
          stroke: '#999',
          strokeDasharray: '5 3',
          strokeWidth: 1
        },
        label: {
          fontSize: 12,
          refX: 0.5,
          refY: 0.9
        }
      },
      data: {
        id,
        isGroup: true
      },
      ports: getRectPort(360, 160)
    })
  }

  // 自动调整连接点（带防抖）
  autoAdjustConnections(node) {
    // 清除之前的定时器
    if (this.adjustTimer) {
      clearTimeout(this.adjustTimer)
    }
    
    // 设置防抖定时器，避免频繁调用
    this.adjustTimer = setTimeout(() => {
      try {
        // 获取与该节点相连的所有边
        const connectedEdges = this.graph.getConnectedEdges(node)
        
        if (connectedEdges.length === 0) return
        
        console.log(`正在为节点 ${node.id} 调整 ${connectedEdges.length} 条连接线的连接点`)
        
        connectedEdges.forEach(edge => {
          const sourceId = edge.getSourceCellId()
          const targetId = edge.getTargetCellId()
          
          // 确定当前节点是源节点还是目标节点
          if (sourceId === node.id) {
            // 当前节点是源节点，调整源连接点
            const targetNode = this.graph.getCellById(targetId)
            if (targetNode) {
              const bestSourcePort = this.findBestPort(node, targetNode)
              const bestTargetPort = this.findBestPort(targetNode, node)
              
              edge.setSource({ cell: sourceId, port: bestSourcePort })
              edge.setTarget({ cell: targetId, port: bestTargetPort })
              
              console.log(`调整边 ${edge.id}: 源端口 ${bestSourcePort}, 目标端口 ${bestTargetPort}`)
            }
          } else if (targetId === node.id) {
            // 当前节点是目标节点，调整目标连接点
            const sourceNode = this.graph.getCellById(sourceId)
            if (sourceNode) {
              const bestSourcePort = this.findBestPort(sourceNode, node)
              const bestTargetPort = this.findBestPort(node, sourceNode)
              
              edge.setSource({ cell: sourceId, port: bestSourcePort })
              edge.setTarget({ cell: targetId, port: bestTargetPort })
              
              console.log(`调整边 ${edge.id}: 源端口 ${bestSourcePort}, 目标端口 ${bestTargetPort}`)
            }
          }
        })
      } catch (error) {
        console.error('自动调整连接点时发生错误:', error)
      }
    }, 100) // 100ms 防抖延迟
  }

  // 找到两个节点之间的最佳连接点
  findBestPort(fromNode, toNode) {
    const fromBBox = fromNode.getBBox()
    const toBBox = toNode.getBBox()
    
    // 计算两个节点的中心点
    const fromCenter = {
      x: fromBBox.x + fromBBox.width / 2,
      y: fromBBox.y + fromBBox.height / 2
    }
    const toCenter = {
      x: toBBox.x + toBBox.width / 2,
      y: toBBox.y + toBBox.height / 2
    }
    
    // 定义所有可能的端口
    const ports = ['port1', 'port2', 'port3', 'port4']
    let bestPort = 'port1'
    let minDistance = Infinity
    let bestScore = -Infinity
    
    // 为每个端口计算得分
    ports.forEach(portId => {
      const fromPortPos = this.getPortPosition(fromNode, portId)
      const distance = Math.sqrt(
        Math.pow(fromPortPos.x - toCenter.x, 2) + 
        Math.pow(fromPortPos.y - toCenter.y, 2)
      )
      
      // 计算方向得分（优先选择朝向目标节点的端口）
      const deltaX = toCenter.x - fromCenter.x
      const deltaY = toCenter.y - fromCenter.y
      
      let directionScore = 0
      switch (portId) {
        case 'port1': // 左侧
          directionScore = deltaX < 0 ? Math.abs(deltaX) : -Math.abs(deltaX) * 0.5
          break
        case 'port2': // 右侧
          directionScore = deltaX > 0 ? Math.abs(deltaX) : -Math.abs(deltaX) * 0.5
          break
        case 'port3': // 上方
          directionScore = deltaY < 0 ? Math.abs(deltaY) : -Math.abs(deltaY) * 0.5
          break
        case 'port4': // 下方
          directionScore = deltaY > 0 ? Math.abs(deltaY) : -Math.abs(deltaY) * 0.5
          break
      }
      
      // 检查是否有其他节点阻挡路径
      const obstacleScore = this.calculateObstacleScore(fromPortPos, toCenter, fromNode, toNode)
      
      // 综合得分：距离越近越好，方向越对越好，阻挡越少越好
      const totalScore = directionScore - distance * 0.1 + obstacleScore
      
      if (totalScore > bestScore) {
        bestScore = totalScore
        bestPort = portId
        minDistance = distance
      }
    })
    
    return bestPort
  }

  // 计算路径阻挡得分
  calculateObstacleScore(fromPos, toPos, fromNode, toNode) {
    const allNodes = this.graph.getNodes()
    let obstacleCount = 0
    
    // 简化的路径检测：检查连线是否穿过其他节点
    allNodes.forEach(node => {
      if (node.id === fromNode.id || node.id === toNode.id) return
      
      const nodeBBox = node.getBBox()
      const nodeCenter = {
        x: nodeBBox.x + nodeBBox.width / 2,
        y: nodeBBox.y + nodeBBox.height / 2
      }
      
      // 检查节点中心是否接近连线路径
      const lineLength = Math.sqrt(
        Math.pow(toPos.x - fromPos.x, 2) + 
        Math.pow(toPos.y - fromPos.y, 2)
      )
      
      if (lineLength > 0) {
        // 计算点到线段的距离
        const A = toPos.y - fromPos.y
        const B = fromPos.x - toPos.x
        const C = toPos.x * fromPos.y - fromPos.x * toPos.y
        const distance = Math.abs(A * nodeCenter.x + B * nodeCenter.y + C) / 
                        Math.sqrt(A * A + B * B)
        
        // 如果距离小于节点的一半宽度，认为有阻挡
        if (distance < Math.max(nodeBBox.width, nodeBBox.height) / 2) {
          obstacleCount++
        }
      }
    })
    
    // 阻挡越少得分越高
    return -obstacleCount * 50
  }

  // 计算端口的实际位置
  getPortPosition(node, portId) {
    const bbox = node.getBBox()
    const ports = {
      'port1': { x: bbox.x, y: bbox.y + bbox.height / 2 }, // 左侧
      'port2': { x: bbox.x + bbox.width, y: bbox.y + bbox.height / 2 }, // 右侧
      'port3': { x: bbox.x + bbox.width / 2, y: bbox.y }, // 上方
      'port4': { x: bbox.x + bbox.width / 2, y: bbox.y + bbox.height } // 下方
    }
    return ports[portId] || ports['port1']
  }
}

export default Group
